import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { EditingBadge, editorsLabel } from "../components/common/EditingBadge";
import { StatusBadge } from "../components/common/StatusBadge";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { SavedLocationSelector, buildLocationKey, matchSavedLocationId } from "../components/common/SavedLocationSelector";
import { OfflineFallbackNotice } from "../components/common/OfflineFallbackNotice";
import { UnsavedChangesDialog } from "../components/common/UnsavedChangesDialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useSyncEditors, useSyncPresenceVersion } from "../hooks/useSyncEngine";
import { createResource, deleteResource, expectedVersionHeaders, extractErrorMessage, fetchLookups, fetchNextLoadNumber, fetchResourceList, updateResource } from "../lib/api";
import { syncEngine } from "../lib/sync/syncEngine";
import { clearSavedPageState, loadSavedPageState, savePageState } from "../lib/filterStorage";
import { clearDraftState, loadDraftState, loadOfflineSnapshot, saveDraftState, saveOfflineSnapshot } from "../lib/offlineState";
import { LoadsFiltersPanel } from "./loads/LoadsFiltersPanel";
import { LoadsTableSection } from "./loads/LoadsTableSection";
import { RepeatTemplatesSection } from "./loads/RepeatTemplatesSection";
import { useLoadsPageEffects } from "./loads/useLoadsPageEffects";
import { useSyncChanges } from "../hooks/useSyncEngine";
import { DEFAULT_PAGE_SIZE, blankLoad, blankRepeatForm, buildDefaultLoadFilters, buildRoundTripStops, createStop, formatDistance, labelizeStopType, loadShipmentDirections, loadShipmentIds, mapLoadStops, normalizeLoadFilterState, originalRouteLabel, repeatGapOptions, shipmentDirectionLabel, toIsoString } from "./loads/loadsPageUtils";
import { getVisibleLoadStatus } from "../lib/statuses";

const LOADS_FILTER_STORAGE_KEY = "loads-page-saved-filters-v1";
const LOADS_LIST_CACHE_TTL_MS = 30 * 60 * 1000;
const LOADS_LIST_CACHE_KEY = ({ filters, page, pageSize, search }) => `lumen-tms-loads-list-v1:${JSON.stringify({ search, filters, page, pageSize })}`;
const LOADS_SHARED_RESOURCES_CACHE_KEY = "lumen-tms-loads-shared-resources-v1";
const LOADS_DRAFT_STORAGE_KEY = "loads-page-editor";

const getDefaultLoadsPageState = () => ({
  search: "",
  filters: normalizeLoadFilterState(buildDefaultLoadFilters()),
  pageSize: DEFAULT_PAGE_SIZE,
});

const getInitialLoadsPageState = () => {
  const state = loadSavedPageState(LOADS_FILTER_STORAGE_KEY, getDefaultLoadsPageState());
  return { ...state, filters: normalizeLoadFilterState(state.filters) };
};

const serializeLoadDraft = (draft) => JSON.stringify(draft || blankLoad);

export const LoadsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";
  const initialPageState = getInitialLoadsPageState();
  const [lookups, setLookups] = useState({ customers: [], trucks: [], trailers: [], drivers: [], statuses: [], currencies: [], stop_types: [], locations: [], shipment_directions: [], distance_unit: "Miles" });
  const [loads, setLoads] = useState([]);
  const [totalLoads, setTotalLoads] = useState(0);
  const [filters, setFilters] = useState(initialPageState.filters);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLoad, setEditingLoad] = useState(null);
  const editors = useSyncEditors("load", dialogOpen ? editingLoad?.id : null);
  useSyncPresenceVersion();

  useEffect(() => {
    if (dialogOpen && editingLoad?.id) {
      syncEngine.setEditing("load", editingLoad.id);
    } else {
      syncEngine.clearEditing();
    }
  }, [dialogOpen, editingLoad?.id]);

  useEffect(() => () => syncEngine.clearEditing(), []);
  const [formState, setFormState] = useState(blankLoad);
  const [saving, setSaving] = useState(false);
  const [repeatTemplates, setRepeatTemplates] = useState([]);
  const [repeatDialogOpen, setRepeatDialogOpen] = useState(false);
  const [repeatTargetLoad, setRepeatTargetLoad] = useState(null);
  const [repeatForm, setRepeatForm] = useState(blankRepeatForm);
  const [repeatSaving, setRepeatSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageState.pageSize || DEFAULT_PAGE_SIZE);
  const [nextLoadNumberPreview, setNextLoadNumberPreview] = useState("");
  const latestLoadsRequestRef = useRef(0);
  const loadFormRef = useRef(null);
  const restoredDraftRef = useRef(false);
  const [initialLoadSnapshot, setInitialLoadSnapshot] = useState(serializeLoadDraft(blankLoad));
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
  const [cacheFallbackState, setCacheFallbackState] = useState({ loads: false, shared: false });
  const loadsListCacheKey = useMemo(() => LOADS_LIST_CACHE_KEY({ search, filters, page: currentPage, pageSize }), [currentPage, filters, pageSize, search]);

  const loadLoads = useCallback(async ({ silent = false } = {}) => {
    const requestId = latestLoadsRequestRef.current + 1;
    latestLoadsRequestRef.current = requestId;
    if (!silent) {
      setLoading(true);
    }
    const cachedPayload = await loadOfflineSnapshot(loadsListCacheKey, LOADS_LIST_CACHE_TTL_MS);
    if (cachedPayload && latestLoadsRequestRef.current === requestId) {
      setLoads(cachedPayload.items || []);
      setTotalLoads(cachedPayload.total_items || 0);
    }
    try {
      const loadPayload = await fetchResourceList("/loads", { search, ...filters, status: filters.status.join(","), page: currentPage, page_size: pageSize });
      if (latestLoadsRequestRef.current !== requestId) {
        return;
      }
      setLoads(loadPayload.items || []);
      setTotalLoads(loadPayload.total_items || 0);
      setCacheFallbackState((current) => ({ ...current, loads: false }));
      await saveOfflineSnapshot(loadsListCacheKey, loadPayload);
    } catch (error) {
      if (latestLoadsRequestRef.current !== requestId) {
        return;
      }
      if (cachedPayload) {
        setLoads(cachedPayload.items || []);
        setTotalLoads(cachedPayload.total_items || 0);
        setCacheFallbackState((current) => ({ ...current, loads: true }));
        toast.warning("Showing cached loads while the live board reconnects.");
      } else {
        toast.error(extractErrorMessage(error, "Unable to load loads data."));
      }
    } finally {
      if (latestLoadsRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [currentPage, filters, loadsListCacheKey, pageSize, search]);

  const loadSharedResources = useCallback(async () => {
    const cachedSharedResources = await loadOfflineSnapshot(LOADS_SHARED_RESOURCES_CACHE_KEY, LOADS_LIST_CACHE_TTL_MS);
    if (cachedSharedResources) {
      setLookups(cachedSharedResources.lookups || { customers: [], trucks: [], trailers: [], drivers: [], statuses: [], currencies: [], stop_types: [], locations: [], shipment_directions: [], distance_unit: "Miles" });
      setRepeatTemplates(cachedSharedResources.repeatTemplates || []);
    }
    try {
      const [lookupPayload, templatePayload] = await Promise.all([fetchLookups(), fetchResourceList("/load-repeat-templates")]);
      setLookups(lookupPayload);
      setRepeatTemplates(templatePayload);
      setCacheFallbackState((current) => ({ ...current, shared: false }));
      await saveOfflineSnapshot(LOADS_SHARED_RESOURCES_CACHE_KEY, { lookups: lookupPayload, repeatTemplates: templatePayload });
    } catch (error) {
      if (cachedSharedResources) {
        setCacheFallbackState((current) => ({ ...current, shared: true }));
        toast.warning("Showing cached load tools while the live board reconnects.");
      } else {
        toast.error(extractErrorMessage(error, "Unable to load shared load tools."));
      }
    }
  }, []);

  const loadsSearchRestoredRef = useRef(false);
  useEffect(() => {
    if (loadsSearchRestoredRef.current) {
      return;
    }
    loadsSearchRestoredRef.current = true;
    const savedSearch = initialPageState.search || "";
    if (searchParams.get("q") || !savedSearch) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("q", savedSearch);
    setSearchParams(nextParams, { replace: true });
    // Only restore a previously "Saved" search once on initial mount — otherwise
    // clearing the search box re-triggers this (searchParams changes) and the saved
    // value immediately reappears.
  }, [initialPageState.search, searchParams, setSearchParams]);

  useLoadsPageEffects({ currentPage, filters, loadLoads, loadSharedResources, pageSize, search, setCurrentPage, totalLoads });
  useSyncChanges(["load"], () => loadLoads({ silent: true }));
  useSyncChanges(["lookups", "repeat_template"], () => loadSharedResources());

  useEffect(() => {
    if (restoredDraftRef.current || dialogOpen) {
      return;
    }
    const storedDraft = loadDraftState(LOADS_DRAFT_STORAGE_KEY);
    if (!storedDraft?.formState) {
      return;
    }
    restoredDraftRef.current = true;
    setEditingLoad(storedDraft.editingLoad || null);
    setFormState(storedDraft.formState);
    setInitialLoadSnapshot(storedDraft.baseSnapshot || serializeLoadDraft(blankLoad));
    setNextLoadNumberPreview(storedDraft.nextLoadNumberPreview || "");
    setDialogOpen(true);
    toast.info(storedDraft.editingLoad ? `Restored an unsaved draft for ${storedDraft.editingLoad.load_number || "this load"}.` : "Restored your unsaved new-load draft.");
  }, [dialogOpen]);

  const canToggleRoundTrip = true;
  const currentLoadSnapshot = useMemo(() => serializeLoadDraft(formState), [formState]);
  const hasUnsavedChanges = dialogOpen && currentLoadSnapshot !== initialLoadSnapshot;
  const dialogOriginalRoute = originalRouteLabel(formState);
  const shipmentIdPreview = editingLoad ? loadShipmentIds(editingLoad) : "Auto after save";
  const dialogShipmentDirection = editingLoad ? loadShipmentDirections(editingLoad) : shipmentDirectionLabel(formState.invoice_pickup_country || formState.stops?.[0]?.country, formState.invoice_delivery_country || formState.stops?.find((stop, index) => index > 0 && ["delivery", "drop"].includes(stop.stop_type))?.country);
  const customerLookupById = useMemo(() => new Map(lookups.customers.map((customer) => [customer.id, customer])), [lookups.customers]);
  const selectedCustomer = lookups.customers.find((customer) => customer.id === formState.customer_id) || null;
  const selectedCustomerAddress = selectedCustomer
    ? [selectedCustomer.billing_address, selectedCustomer.city, selectedCustomer.province_or_state, selectedCustomer.postal_code, selectedCustomer.country].filter(Boolean).join(", ")
    : "Select a customer to view the address.";
  const customerOptions = useMemo(() => [{ value: "", label: "Select customer", searchText: "select customer" }, ...lookups.customers.map((customer) => ({
    value: customer.id,
    label: customer.customer_name,
    meta: [customer.city, customer.province_or_state].filter(Boolean).join(", "),
    searchText: `${customer.customer_name} ${customer.billing_address || ""} ${customer.city || ""}`,
  }))], [lookups.customers]);

  const routeHint = useMemo(
    () => "Assignments and actual arrival / departure timestamps are managed only from the Dispatch Board. Loads is now for load details, pickups, deliveries, and planned timing.",
    [],
  );

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }
    if (!hasUnsavedChanges) {
      clearDraftState(LOADS_DRAFT_STORAGE_KEY);
      return;
    }
    saveDraftState(LOADS_DRAFT_STORAGE_KEY, {
      baseSnapshot: initialLoadSnapshot,
      editingLoad,
      formState,
      nextLoadNumberPreview,
    });
  }, [dialogOpen, editingLoad, formState, hasUnsavedChanges, initialLoadSnapshot, nextLoadNumberPreview]);

  const updateFilter = (event) => {
    setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const toggleStatusFilter = (statusValue) => {
    setFilters((current) => ({
      ...current,
      status: current.status.includes(statusValue)
        ? current.status.filter((item) => item !== statusValue)
        : [...current.status, statusValue],
    }));
  };

  const toggleSort = (column) => {
    setFilters((current) => ({
      ...current,
      sort_by: column,
      sort_dir: current.sort_by === column && current.sort_dir === "asc" ? "desc" : "asc",
    }));
  };

  const handleSaveFilters = () => {
    savePageState(LOADS_FILTER_STORAGE_KEY, { search, filters, pageSize });
    toast.success("Loads filters saved for this browser.");
  };

  const handleResetSavedFilters = () => {
    const defaultState = getDefaultLoadsPageState();
    clearSavedPageState(LOADS_FILTER_STORAGE_KEY);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("q");
    setSearchParams(nextParams, { replace: true });
    setFilters(defaultState.filters);
    setPageSize(defaultState.pageSize);
    setCurrentPage(1);
    toast.success("Saved loads filters cleared.");
  };

  const resetDialog = () => {
    setUnsavedChangesDialogOpen(false);
    setDialogOpen(false);
    setEditingLoad(null);
    setNextLoadNumberPreview("");
    setFormState(blankLoad);
    setInitialLoadSnapshot(serializeLoadDraft(blankLoad));
    clearDraftState(LOADS_DRAFT_STORAGE_KEY);
  };

  const requestDialogClose = () => {
    if (saving) {
      return;
    }
    if (hasUnsavedChanges) {
      setUnsavedChangesDialogOpen(true);
      return;
    }
    resetDialog();
  };

  const resetRepeatDialog = () => {
    setRepeatDialogOpen(false);
    setRepeatTargetLoad(null);
    setRepeatForm(blankRepeatForm);
  };

  const openCreateDialog = () => {
    const nextDraft = { ...blankLoad };
    clearDraftState(LOADS_DRAFT_STORAGE_KEY);
    setEditingLoad(null);
    setFormState(nextDraft);
    setInitialLoadSnapshot(serializeLoadDraft(nextDraft));
    setDialogOpen(true);
    fetchNextLoadNumber()
      .then((payload) => setNextLoadNumberPreview(payload.next_load_number || ""))
      .catch(() => setNextLoadNumberPreview(""));
  };

  const openEditDialog = (load) => {
    const nextDraft = {
      ...blankLoad,
      ...load,
      is_round_trip: Boolean(load.is_round_trip),
      customer_reference_number: load.customer_reference_number || "",
      rate: load.rate,
      notes: load.notes || "",
      stops: mapLoadStops(load, lookups.locations),
    };
    clearDraftState(LOADS_DRAFT_STORAGE_KEY);
    setEditingLoad(load);
    setNextLoadNumberPreview("");
    setFormState(nextDraft);
    setInitialLoadSnapshot(serializeLoadDraft(nextDraft));
    setDialogOpen(true);
  };

  const openRepeatDialog = (load) => {
    setRepeatTargetLoad(load);
    setRepeatForm({ ...blankRepeatForm, template_name: `${load.load_number} repeat`, start_date: new Date().toISOString().slice(0, 10) });
    setRepeatDialogOpen(true);
  };

  const updateForm = (event) => {
    if (event.target.name === "is_round_trip") {
      const nextRoundTrip = event.target.value === "yes";
      if (nextRoundTrip !== formState.is_round_trip) {
        if (nextRoundTrip && formState.stops.length > 2 && !formState.is_round_trip && !window.confirm("Switching this load to round trip will rebuild the route from the first pickup and first delivery. Continue?")) {
          return;
        }
        if (!nextRoundTrip && formState.is_round_trip && formState.stops.length > 2 && !window.confirm("Switching this load to one-way will keep only the outbound pickup and delivery stops. Continue?")) {
          return;
        }
      }
      setFormState((current) => ({
        ...current,
        is_round_trip: nextRoundTrip,
        stops: nextRoundTrip ? buildRoundTripStops(current.stops) : current.stops.slice(0, 2),
      }));
      return;
    }
    setFormState((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const updateRepeatForm = (event) => {
    setRepeatForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const updateStop = (index, field, value) => {
    setFormState((current) => {
      const nextStops = current.stops.map((stop, stopIndex) => {
        if (stopIndex !== index) {
          return stop;
        }
        return {
          ...stop,
          [field]: value,
          auto_round_trip_time: field === "planned_datetime" ? stop.auto_round_trip_time : false,
        };
      });

      if (current.is_round_trip && (index === 0 || index === 1) && nextStops.length >= 4) {
        const rebuilt = buildRoundTripStops(nextStops);
        if (nextStops[2].auto_round_trip_time) {
          nextStops[2] = { ...nextStops[2], ...rebuilt[2], location_id: nextStops[2].location_id, original_location_id: nextStops[2].original_location_id, original_location_key: nextStops[2].original_location_key, auto_round_trip_time: true };
        }
        if (nextStops[3].auto_round_trip_time) {
          nextStops[3] = { ...nextStops[3], ...rebuilt[3], location_id: nextStops[3].location_id, original_location_id: nextStops[3].original_location_id, original_location_key: nextStops[3].original_location_key, auto_round_trip_time: true };
        }
      }

      return { ...current, stops: nextStops };
    });
  };

  const applySavedLocation = (index, locationId) => {
    const location = lookups.locations.find((item) => item.id === locationId);
    if (!location) {
      return;
    }
    const currentStop = formState.stops[index];
    if (
      currentStop?.original_location_key
      && buildLocationKey(location) !== currentStop.original_location_key
      && !window.confirm("Confirm changing this stop from the original saved location?")
    ) {
      return;
    }
    setFormState((current) => ({
      ...current,
      stops: current.stops.map((stop, stopIndex) => {
        if (stopIndex === index) {
          return { ...stop, ...location, location_id: location.id };
        }
        if (current.is_round_trip && index === 0 && stopIndex === current.stops.length - 1) {
          return { ...stop, ...location, location_id: location.id, stop_type: "delivery" };
        }
        if (current.is_round_trip && index === 1 && stopIndex === 2) {
          return { ...stop, ...location, location_id: location.id, stop_type: "pickup" };
        }
        return stop;
      }),
    }));
  };

  const handleLocationCreated = (index, location) => {
    setLookups((current) => ({ ...current, locations: [...current.locations, location].sort((left, right) => left.company_name.localeCompare(right.company_name)) }));
    applySavedLocation(index, location.id);
  };

  const addStop = (stopType = "drop") => {
    setFormState((current) => {
      const nextStops = [...current.stops];
      nextStops.splice(Math.max(nextStops.length - 1, 1), 0, createStop(stopType));
      return { ...current, stops: nextStops };
    });
  };

  const openDispatchBoard = (load, tripId = null) => {
    const params = new URLSearchParams({ q: load.load_number });
    if (tripId) {
      params.set("trip_id", tripId);
    }
    navigate(`/dispatch-board?${params.toString()}`);
  };

  const removeStop = (index) => {
    setFormState((current) => {
      if (current.stops.length <= 2) {
        return current;
      }
      const nextStops = current.stops.filter((_, stopIndex) => stopIndex !== index);
      nextStops[0] = { ...nextStops[0], stop_type: "pickup" };
      nextStops[nextStops.length - 1] = {
        ...nextStops[nextStops.length - 1],
        stop_type: ["delivery", "drop"].includes(nextStops[nextStops.length - 1].stop_type) ? nextStops[nextStops.length - 1].stop_type : "delivery",
      };
      return { ...current, stops: nextStops };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const stops = formState.stops.map((stop, index, rows) => {
        const { location_id, original_location_id, original_location_key, auto_round_trip_time, ...stopWithoutMeta } = stop;
        return ({
        ...stopWithoutMeta,
        stop_type: index === 0 ? "pickup" : index === rows.length - 1 && !["delivery", "drop"].includes(stop.stop_type) ? "delivery" : stop.stop_type,
        planned_datetime: toIsoString(stop.planned_datetime),
        actual_arrived_at: toIsoString(stop.actual_arrived_at) || null,
        actual_departed_at: toIsoString(stop.actual_departed_at) || null,
        contact_name: stop.contact_name || null,
        phone: stop.phone || null,
        postal_code: stop.postal_code || null,
        truck_id: stop.truck_id || null,
        trailer_id: stop.trailer_id || null,
        driver_id: stop.driver_id || null,
        notes: stop.notes || null,
      });});

      const firstStop = stops[0];
      const lastStop = stops[stops.length - 1];
      const payload = {
        load_number: formState.load_number || null,
        customer_reference_number: formState.customer_reference_number,
        customer_id: formState.customer_id,
        pickup_company: firstStop.company_name,
        pickup_contact: firstStop.contact_name,
        pickup_phone: firstStop.phone,
        pickup_address: firstStop.address,
        pickup_city: firstStop.city,
        pickup_state_province: firstStop.state_province,
        pickup_postal_code: firstStop.postal_code,
        pickup_country: firstStop.country,
        pickup_datetime: firstStop.planned_datetime,
        pickup_actual_arrived_at: firstStop.actual_arrived_at,
        pickup_actual_departed_at: firstStop.actual_departed_at,
        delivery_company: lastStop.company_name,
        delivery_contact: lastStop.contact_name,
        delivery_phone: lastStop.phone,
        delivery_address: lastStop.address,
        delivery_city: lastStop.city,
        delivery_state_province: lastStop.state_province,
        delivery_postal_code: lastStop.postal_code,
        delivery_country: lastStop.country,
        delivery_datetime: lastStop.planned_datetime,
        delivery_actual_arrived_at: lastStop.actual_arrived_at,
        delivery_actual_departed_at: lastStop.actual_departed_at,
        truck_id: null,
        trailer_id: null,
        driver_id: null,
        rate: Number(formState.rate || 0),
        currency: formState.currency,
        is_round_trip: Boolean(formState.is_round_trip),
        status: editingLoad?.status || "Booked",
        notes: formState.notes || null,
        stops,
      };

      const savedLoad = editingLoad
        ? await updateResource("/loads", editingLoad.id, payload, expectedVersionHeaders(editingLoad))
        : await createResource("/loads", payload);
      if (savedLoad.distance_alerts?.length) {
        toast.warning(savedLoad.distance_alerts[0]);
      }
      if (savedLoad.conflict) {
        toast.warning(`${savedLoad.conflict.other_user_name || "Another dispatcher"} also changed this load. ${savedLoad.conflict.summary}`, { duration: 9000 });
      }
      if (editingLoad) {
        toast.success("Load updated.");
        await loadLoads();
      } else {
        toast.success(`Load ${savedLoad.load_number} created. Showing the saved record now.`);
        navigate(`/loads?q=${encodeURIComponent(savedLoad.load_number)}`);
      }
      setUnsavedChangesDialogOpen(false);
      resetDialog();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to save this load."));
    } finally {
      setSaving(false);
    }
  };

  const handleRepeatSubmit = async (event) => {
    event.preventDefault();
    if (!repeatTargetLoad) {
      return;
    }
    try {
      setRepeatSaving(true);
      const payload = {
        mode: repeatForm.mode,
        template_name: repeatForm.template_name || null,
        start_date: repeatForm.start_date,
        copies_count: Number(repeatForm.copies_count),
        interval_days: Number(repeatForm.interval_days),
      };
      const result = await createResource(`/loads/${repeatTargetLoad.id}/repeat`, payload);
      toast.success(result.mode === "save_template" ? "Repeat template saved." : `${result.created_count} repeated load(s) created.`);
      resetRepeatDialog();
      await Promise.all([loadLoads(), loadSharedResources()]);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to create repeated loads."));
    } finally {
      setRepeatSaving(false);
    }
  };

  const handleGenerateTemplate = async (template) => {
    try {
      const result = await createResource(`/load-repeat-templates/${template.id}/generate`, { start_date: template.start_date, copies_count: template.copies_count });
      toast.success(`${result.created_count} repeated load(s) created from template.`);
      loadLoads();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to generate repeated loads from this template."));
    }
  };

  const handleDeleteTemplate = async (template) => {
    try {
      await deleteResource("/load-repeat-templates", template.id);
      toast.success("Repeat template deleted.");
      loadSharedResources();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to delete this repeat template."));
    }
  };

  const handleDelete = async (load) => {
    if (!window.confirm(`Cancel load ${load.load_number}? It will move to the Canceled filter and its load number will never be reused.`)) {
      return;
    }
    try {
      await deleteResource("/loads", load.id);
      toast.success(`Load ${load.load_number} canceled. Find it anytime under the Canceled status filter.`);
      loadLoads();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to cancel this load."));
    }
  };

  return (
    <div className="space-y-6" data-testid="loads-page">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Load board</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Loads</h1>
        </div>
        <button
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800"
          data-testid="loads-create-button"
          onClick={openCreateDialog}
          type="button"
        >
          <Plus className="h-4 w-4" />
          Create load
        </button>
      </section>

      <LoadsFiltersPanel filters={filters} lookups={lookups} onResetSavedFilters={handleResetSavedFilters} onSaveFilters={handleSaveFilters} setFilters={setFilters} toggleStatusFilter={toggleStatusFilter} updateFilter={updateFilter} />

      <OfflineFallbackNotice
        message={cacheFallbackState.loads ? "Showing cached Loads data while the connection recovers." : cacheFallbackState.shared ? "Showing cached load tools and repeat templates while the connection recovers." : ""}
        testId="loads-offline-fallback-notice"
      />

      <RepeatTemplatesSection handleDeleteTemplate={handleDeleteTemplate} handleGenerateTemplate={handleGenerateTemplate} repeatTemplates={repeatTemplates} />

      <LoadsTableSection currentPage={currentPage} customerLookupById={customerLookupById} filters={filters} handleDelete={handleDelete} loading={loading} loads={loads} lookups={lookups} openDispatchBoard={openDispatchBoard} openEditDialog={openEditDialog} openRepeatDialog={openRepeatDialog} pageSize={pageSize} setCurrentPage={setCurrentPage} setPageSize={setPageSize} toggleSort={toggleSort} totalLoads={totalLoads} />

      <Sheet onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          requestDialogClose();
        }
      }} open={dialogOpen}>
        <SheetContent className="w-full overflow-hidden border-zinc-300 bg-white p-0 sm:max-w-5xl" data-testid="load-dialog-overlay" side="right">
          <div className="flex h-full flex-col">
            <SheetHeader className="sr-only">
              <SheetTitle>{editingLoad ? `Edit load ${editingLoad.load_number}` : "Create load"}</SheetTitle>
              <SheetDescription>Load details now open in a right-side editable panel.</SheetDescription>
            </SheetHeader>
            <div className="flex items-start justify-between gap-4 border-b border-zinc-200 px-6 py-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{editingLoad ? "Edit load" : "New load"}</p>
                <h2 className="mt-2 flex flex-wrap items-center gap-2 text-2xl font-semibold text-zinc-900">
                  <span>{editingLoad ? editingLoad.load_number : "Create load"}</span>
                  <EditingBadge editors={editors} testId="load-dialog-editing-badge" />
                </h2>
                {editors.length ? <p className="mt-1 text-xs text-violet-800" data-testid="load-dialog-editing-note">{editorsLabel(editors)} this load right now — saves from both of you are merged automatically.</p> : null}
              </div>
              <button className="rounded-full border border-zinc-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="load-dialog-close-button" onClick={requestDialogClose} type="button">Close</button>
            </div>

            <form className="flex min-h-0 flex-1 flex-col" data-testid="load-dialog-form" onSubmit={handleSubmit} ref={loadFormRef}>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
              <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="load-core-fields-section">
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Load number</span>
                  <input className="w-full rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-500" data-testid="load-field-load_number" disabled readOnly type="text" value={formState.load_number || nextLoadNumberPreview || "Auto-generated on save"} />
                  {!editingLoad ? <p className="text-xs text-zinc-500" data-testid="load-field-load-number-hint">Next generated load number in sequence.</p> : null}
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Customer</span>
                  <SearchableSelect dataTestId="load-field-customer_id" onChange={(value) => setFormState((current) => ({ ...current, customer_id: value }))} options={customerOptions} placeholder="Select customer" searchPlaceholder="Search customers..." value={formState.customer_id} />
                </label>
                <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 md:col-span-2" data-testid="load-customer-address-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Customer address</p>
                  <p className="mt-2 text-sm font-medium text-zinc-900" data-testid="load-customer-address-value">{selectedCustomerAddress}</p>
                </div>
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Customer reference #</span>
                  <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="load-field-customer_reference_number" name="customer_reference_number" onChange={updateForm} required type="text" value={formState.customer_reference_number} />
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Rate / revenue</span>
                  <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="load-field-rate" min="0" name="rate" onChange={updateForm} required step="0.01" type="number" value={formState.rate} />
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Currency</span>
                  <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="load-field-currency" name="currency" onChange={updateForm} required value={formState.currency}>
                    {lookups.currencies.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
                  </select>
                </label>
                {canToggleRoundTrip ? (
                  <label className="block space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Round trip?</span>
                    <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="load-field-round-trip" name="is_round_trip" onChange={updateForm} value={formState.is_round_trip ? "yes" : "no"}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </label>
                ) : (
                  <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3" data-testid="load-field-round-trip-readonly">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Round trip?</p>
                    <p className="mt-2 text-sm text-zinc-700">{formState.is_round_trip ? "Yes" : "No"}</p>
                  </div>
                )}
                <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 md:col-span-2" data-testid="load-original-route-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Original route</p>
                  <p className="mt-2 text-sm font-semibold text-zinc-900" data-testid="load-original-route-value">{dialogOriginalRoute}</p>
                  <p className="mt-1 text-sm text-zinc-500" data-testid="load-round-trip-indicator">Round trip: {formState.is_round_trip ? "Yes" : "No"}</p>
                </div>
                <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3" data-testid="load-shipment-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Shipment</p>
                  <p className="mt-2 text-sm font-semibold text-zinc-900" data-testid="load-shipment-id-value">{shipmentIdPreview}</p>
                  <p className="mt-1 text-sm text-zinc-500" data-testid="load-shipment-direction-value">{dialogShipmentDirection}</p>
                </div>
                <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3" data-testid="load-field-status-readonly">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Status</p>
                  <div className="mt-2"><StatusBadge status={editingLoad ? getVisibleLoadStatus(editingLoad) : "Unassigned"} testId="load-status-readonly-badge" /></div>
                </div>
              </section>

              <section className="space-y-4" data-testid="load-stops-section">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Route stops</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="load-add-pickup-button" onClick={() => addStop("pickup")} type="button">
                      <Plus className="h-4 w-4" /> Add pickup
                    </button>
                    <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="load-add-delivery-button" onClick={() => addStop("delivery")} type="button">
                      <Plus className="h-4 w-4" /> Add delivery
                    </button>
                    <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="load-add-drop-button" onClick={() => addStop("drop")} type="button">
                      <Plus className="h-4 w-4" /> Add drop
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {formState.stops.map((stop, index) => {
                    const isFirst = index === 0;
                    const isLast = index === formState.stops.length - 1;
                    const allowedTypes = isFirst ? ["pickup"] : isLast ? ["delivery", "drop"] : ["pickup", "drop", "delivery"];

                    return (
                      <div className="rounded-3xl border border-zinc-200 bg-white p-5" data-testid={`load-stop-card-${index}`} key={`stop-${index}`}>
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Stop {index + 1}</p>
                            <h3 className="mt-2 text-lg font-semibold text-zinc-900">{isFirst ? "Pickup" : isLast ? "Final stop" : labelizeStopType(stop.stop_type)}</h3>
                          </div>
                          {!isFirst && !isLast ? (
                            <button className="inline-flex items-center gap-2 rounded-2xl border border-red-200 px-4 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-50" data-testid={`load-remove-stop-button-${index}`} onClick={() => removeStop(index)} type="button">
                              <Trash2 className="h-4 w-4" /> Remove stop
                            </button>
                          ) : null}
                        </div>

                        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                          <div className="xl:col-span-3">
                            <SavedLocationSelector compact={false} index={index} locations={lookups.locations} onLocationCreated={handleLocationCreated} onSelect={applySavedLocation} stop={stop} testIdPrefix="load-stop" />
                          </div>
                          <label className="block space-y-2">
                            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Stop type</span>
                            <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid={`load-stop-type-${index}`} onChange={(event) => updateStop(index, "stop_type", event.target.value)} value={isFirst ? "pickup" : stop.stop_type}>
                              {allowedTypes.map((typeValue) => <option key={typeValue} value={typeValue}>{labelizeStopType(typeValue)}</option>)}
                            </select>
                          </label>
                          <label className="block space-y-2">
                            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Planned date & time</span>
                            <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid={`load-stop-planned-${index}`} onChange={(event) => updateStop(index, "planned_datetime", event.target.value)} required type="datetime-local" value={stop.planned_datetime} />
                          </label>
                          <label className="block space-y-2 md:col-span-2 xl:col-span-4">
                            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Stop notes</span>
                            <textarea className="min-h-[96px] w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid={`load-stop-notes-${index}`} onChange={(event) => updateStop(index, "notes", event.target.value)} value={stop.notes} />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {editingLoad?.dispatch_trips?.length ? (
                <section className="space-y-3" data-testid="load-dispatch-trip-summary-section">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Dispatch trips</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {editingLoad.dispatch_trips.map((trip) => (
                      <button className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-4 text-left transition hover:border-zinc-900 hover:bg-white" data-testid={`load-dispatch-trip-${trip.id}`} key={trip.id} onClick={() => openDispatchBoard(editingLoad, trip.id)} type="button">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-semibold text-zinc-900">{trip.trip_number}</p>
                          <StatusBadge status={trip.dispatch_status} testId={`load-dispatch-trip-status-${trip.id}`} />
                        </div>
                        <p className="mt-2 text-sm text-zinc-600">Stop {trip.start_stop_order} → Stop {trip.end_stop_order}</p>
                        <p className="mt-1 text-sm text-zinc-500">{trip.driver_name || "No driver"} / {trip.truck_number || "No truck"} / {trip.trailer_number || "No trailer"}</p>
                        <p className="mt-1 text-xs text-zinc-500" data-testid={`load-dispatch-trip-distance-${trip.id}`}>{formatDistance(trip.distance_miles, lookups.distance_unit)}</p>
                        <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Open dispatch details</p>
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <button className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="load-dialog-open-dispatch-board-button" onClick={() => openDispatchBoard(editingLoad)} type="button">Dispatch this load</button>
                  </div>
                </section>
              ) : null}

              <label className="block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Notes</span>
                <textarea className="min-h-[120px] w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="load-field-notes" name="notes" onChange={updateForm} value={formState.notes} />
              </label>

              </div>

              <div className="flex shrink-0 flex-wrap justify-end gap-3 border-t border-zinc-200 bg-white px-6 py-4" data-testid="load-dialog-sticky-footer">
                <button className="rounded-2xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-600 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="load-dialog-cancel-button" onClick={requestDialogClose} type="button">Cancel</button>
                <button className="rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-70" data-testid="load-dialog-save-button" disabled={saving} type="submit">{saving ? "Saving..." : editingLoad ? "Save load" : "Create load"}</button>
              </div>
            </form>
          </div>
        </SheetContent>
      </Sheet>

      <UnsavedChangesDialog
        description="You have unsaved changes in this load. Save them before closing, or discard them."
        onDiscard={resetDialog}
        onKeepEditing={() => setUnsavedChangesDialogOpen(false)}
        onSaveAndClose={() => {
          setUnsavedChangesDialogOpen(false);
          loadFormRef.current?.requestSubmit();
        }}
        open={unsavedChangesDialogOpen}
        saveDisabled={saving}
        testIdPrefix="load-unsaved-changes"
        title="Unsaved load changes"
      />

      {repeatDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/45 px-4 py-6" data-testid="repeat-load-dialog-overlay">
          <div className="panel-surface w-full max-w-2xl px-6 py-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Repeat load</p>
                <h2 className="mt-2 text-2xl font-semibold text-zinc-900">{repeatTargetLoad?.load_number}</h2>
              </div>
              <button className="rounded-full border border-zinc-200 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="repeat-load-dialog-close" onClick={resetRepeatDialog} type="button">Close</button>
            </div>

            <form className="mt-6 space-y-4" data-testid="repeat-load-dialog-form" onSubmit={handleRepeatSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Mode</span>
                  <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="repeat-load-mode" name="mode" onChange={updateRepeatForm} value={repeatForm.mode}>
                    <option value="create_now">Create copies now</option>
                    <option value="save_template">Save template for later</option>
                  </select>
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Start date</span>
                  <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="repeat-load-start-date" name="start_date" onChange={updateRepeatForm} required type="date" value={repeatForm.start_date} />
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">How many copies</span>
                  <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="repeat-load-copies-count" min="1" name="copies_count" onChange={updateRepeatForm} required type="number" value={repeatForm.copies_count} />
                </label>
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Gap pattern</span>
                  <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="repeat-load-interval-days" name="interval_days" onChange={updateRepeatForm} value={repeatForm.interval_days}>
                    {repeatGapOptions.map((option) => <option key={option} value={option}>{option === 1 ? "Daily / no gap" : `Every ${option} days`}</option>)}
                  </select>
                </label>
              </div>

              {repeatForm.mode === "save_template" ? (
                <label className="block space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Template name</span>
                  <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="repeat-load-template-name" name="template_name" onChange={updateRepeatForm} required type="text" value={repeatForm.template_name} />
                </label>
              ) : null}
              <div className="flex flex-wrap justify-end gap-3">
                <button className="rounded-2xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-600 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="repeat-load-cancel-button" onClick={resetRepeatDialog} type="button">Cancel</button>
                <button className="rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-70" data-testid="repeat-load-save-button" disabled={repeatSaving} type="submit">{repeatSaving ? "Saving..." : repeatForm.mode === "save_template" ? "Save template" : "Create copies"}</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};